import {
    ClientsWorkOverviewColumn,
    ClientsWorkOverviewSortValue,
    DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
} from "@common/types/views/clientsWorkOverviewView";
import { ViewConfig } from "@lib/views";
import {
    CloseOverviewMember,
    CloseOverviewEstimatedTimeByMember,
    CloseOverviewOpenRoles,
    CloseOverviewWorkspaceRow,
    CloseOverviewEntityRow,
    CloseOverviewCloseRow,
} from "@common/schemas/clientsCloseOverview.schema";
import { CloseApplicationType } from "@common/types/closeApplicationType";
import { CloseHealth, CloseHealthReason } from "@common/types/closeHealth";
import { WorkClientNameCell } from "./cells/WorkClientNameCell";
import { WorkEntityCell } from "./cells/WorkEntityCell";
import { WorkWorkspaceNameCell } from "./cells/WorkWorkspaceNameCell";
import {
    WorkCloseStartDateCell,
    WorkCloseEndDateCell,
    WorkAppliedAtCell,
} from "./cells/WorkCloseDateCell";
import { WorkStartDateCell } from "./cells/WorkStartDateCell";
import { WorkDeliveryDateCell } from "./cells/WorkDeliveryDateCell";
import { WorkServicePeriodCell } from "./cells/WorkServicePeriodCell";
import { WorkCloseStageCell } from "./cells/WorkCloseStageCell";
import { WorkTimeInStageCell } from "./cells/WorkTimeInStageCell";
import { WorkPreparerReviewerCell } from "./cells/WorkPreparerReviewerCell";
import { WorkMyRoleCell } from "./cells/WorkMyRoleCell";
import { WorkActionsCell } from "./cells/WorkActionsCell";
import { WorkTaskProgressCell } from "./cells/WorkTaskProgressCell";
import { WorkEstimatedTimeCell } from "./cells/WorkEstimatedTimeCell";
import { WorkAppliedByCell } from "./cells/WorkAppliedByCell";
import { WorkUnreadTasksCell } from "./cells/WorkUnreadTasksCell";
import { WorkApplicationTypeCell } from "./cells/WorkApplicationTypeCell";
import { WorkHealthCell } from "./cells/WorkHealthCell";

// ─────────────────────────────────────────────────────────────
// Row types
// ─────────────────────────────────────────────────────────────

/** Top-level row used when grouping by close template */
export interface ClientsWorkOverviewGroupRow {
    id: string;
    rowType: "group";
    name: string;
    /** Shown after the name, e.g. a person group's "3 · 2h left · 1 at risk". */
    summary?: string;
    children: ClientsWorkOverviewWorkRow[];
}

/** Top-level row: one per client workspace */
export interface ClientsWorkOverviewWorkspaceRow {
    id: string;
    rowType: "workspace";
    name: string;
    workspaceId: string;
    children: ClientsWorkOverviewWorkRow[];
}

/**
 * Second-level row: one per (entity × close) combination.
 * Carries both entity icon data and close data so the whole
 * row can be rendered on a single line.
 */
export interface ClientsWorkOverviewWorkRow {
    id: string;
    rowType: "work";
    /** Close name / close template name */
    name: string;
    // entity fields (icon only)
    entityId: number;
    workspaceId: string;
    entityIconColor: string;
    entityLogo: string | null;
    entityName: string;
    entityIsBusiness: boolean;
    /** Name of the parent client workspace — carried on the row for the Client column. */
    workspaceName: string;
    // close fields
    closeId: string;
    startDate: string | null;
    endDate: string | null;
    workStartDate: string | null;
    workDeliveryDate: string | null;
    closeStageId: string | null;
    closeStageName: string | null;
    closeTemplateId: string;
    /** Set when this close was created by the automatic recurrence occurrence apply job. */
    recurrenceId: string | null;
    closeStageSetAt: string | null;
    isFinished: boolean;
    cancelledAt: string | null;
    terminatedTasksCount: number;
    totalTaskCount: number;
    estimatedTimeRemaining: number;
    estimatedTimeDone: number;
    estimatedTimeByMember: CloseOverviewEstimatedTimeByMember[];
    preparers: CloseOverviewMember[];
    reviewers: CloseOverviewMember[];
    managers: CloseOverviewMember[];
    /** Per role, who holds the open tasks a change to it would reassign. */
    openRoles: CloseOverviewOpenRoles;
    createdByUserId: number | null;
    createdAt: string;
    unreadTaskCount: number;
    applicationType: CloseApplicationType;
    health: CloseHealth;
    healthReason: CloseHealthReason | null;
    /** True while an agent run on this close is in the `running` state. */
    isAgentRunning: boolean;
    // required for ViewConfig nesting (no children at this level)
    children?: never;
}

export type ClientsWorkOverviewRow =
    | ClientsWorkOverviewWorkspaceRow
    | ClientsWorkOverviewGroupRow
    | ClientsWorkOverviewWorkRow;

// ─────────────────────────────────────────────────────────────
// Mappers
// ─────────────────────────────────────────────────────────────

export function mapWorkspaceToWorkRow(
    ws: CloseOverviewWorkspaceRow,
): ClientsWorkOverviewWorkspaceRow {
    const rawId = ws.id.startsWith("workspace:")
        ? ws.id.slice("workspace:".length)
        : ws.id;

    const children: ClientsWorkOverviewWorkRow[] = [];
    for (const entity of ws.children) {
        for (const close of entity.children) {
            children.push(
                mapWorkRow({
                    entity,
                    close,
                    workspaceId: rawId,
                    workspaceName: ws.name,
                }),
            );
        }
    }

    return {
        id: ws.id,
        rowType: "workspace",
        name: ws.name,
        workspaceId: rawId,
        children,
    };
}

function mapWorkRow({
    entity,
    close,
    workspaceId,
    workspaceName,
}: {
    entity: CloseOverviewEntityRow;
    close: CloseOverviewCloseRow;
    workspaceId: string;
    workspaceName: string;
}): ClientsWorkOverviewWorkRow {
    return {
        id: `${entity.id}:${close.id}`,
        rowType: "work",
        name: close.name,
        entityId: Number(entity.id.replace("entity:", "")),
        workspaceId,
        workspaceName,
        entityIconColor: entity.iconColor,
        entityLogo: entity.logo,
        entityName: entity.name ?? "",
        entityIsBusiness: entity.isBusiness,
        startDate: close.startDate,
        endDate: close.endDate,
        workStartDate: close.workStartDate,
        workDeliveryDate: close.workDeliveryDate,
        closeStageId: close.closeStageId,
        closeStageName: close.closeStageName,
        closeStageSetAt: close.closeStageSetAt,
        isFinished: close.isFinished,
        cancelledAt: close.cancelledAt ?? null,
        terminatedTasksCount: close.terminatedTasksCount,
        totalTaskCount: close.totalTaskCount,
        estimatedTimeRemaining: close.estimatedTimeRemaining,
        estimatedTimeDone: close.estimatedTimeDone,
        estimatedTimeByMember: close.estimatedTimeByMember,
        preparers: close.preparers,
        reviewers: close.reviewers,
        managers: close.managers ?? [],
        openRoles: close.openRoles,
        closeId: close.id,
        closeTemplateId: close.closeTemplateId,
        recurrenceId: close.recurrenceId,
        createdByUserId: close.createdByUserId,
        createdAt: close.createdAt,
        unreadTaskCount: 0,
        applicationType: close.applicationType,
        health: close.health,
        healthReason: close.healthReason,
        isAgentRunning: close.isAgentRunning,
    };
}

// ─────────────────────────────────────────────────────────────
// View config
// ─────────────────────────────────────────────────────────────

export const clientsWorkOverviewViewConfig: ViewConfig<
    ClientsWorkOverviewRow,
    ClientsWorkOverviewColumn,
    ClientsWorkOverviewSortValue
> = {
    columns: {
        [ClientsWorkOverviewColumn.CLIENT]: {
            accessorKey: "name",
            name: "Service",
            header: "Service",
            cell: WorkClientNameCell,
            immutable: true,
            sort: {
                asc: ClientsWorkOverviewSortValue.SERVICE_NAME_ASC,
                desc: ClientsWorkOverviewSortValue.SERVICE_NAME_DESC,
                defaultOrder: "asc",
            },
            minSize: 280,
            maxSize: 700,
        },
        [ClientsWorkOverviewColumn.WORKSPACE_NAME]: {
            accessorKey: "workspaceName",
            name: "Client",
            header: "Client",
            cell: WorkWorkspaceNameCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.NAME_ASC,
                desc: ClientsWorkOverviewSortValue.NAME_DESC,
                defaultOrder: "asc",
            },
            minSize: 160,
            maxSize: 280,
        },
        [ClientsWorkOverviewColumn.ENTITY]: {
            accessorKey: "entityId",
            name: "Entity",
            header: "Entity",
            cell: WorkEntityCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.ENTITY_ASC,
                desc: ClientsWorkOverviewSortValue.ENTITY_DESC,
                defaultOrder: "asc",
            },
            minSize: 160,
            maxSize: 280,
        },
        [ClientsWorkOverviewColumn.CLOSE_START]: {
            accessorKey: "startDate",
            name: "Service start",
            header: "Service start",
            cell: WorkCloseStartDateCell,
            hiddenFromSettings: true,
            minSize: 140,
            maxSize: 180,
        },
        [ClientsWorkOverviewColumn.CLOSE_END]: {
            accessorKey: "endDate",
            name: "Service end",
            header: "Service end",
            cell: WorkCloseEndDateCell,
            hiddenFromSettings: true,
            minSize: 140,
            maxSize: 180,
        },
        [ClientsWorkOverviewColumn.SERVICE_PERIOD]: {
            accessorKey: "startDate",
            name: "Service period",
            header: "Service period",
            cell: WorkServicePeriodCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.SERVICE_PERIOD_ASC,
                desc: ClientsWorkOverviewSortValue.SERVICE_PERIOD_DESC,
            },
            minSize: 160,
            maxSize: 240,
        },
        [ClientsWorkOverviewColumn.WORK_START_DATE]: {
            accessorKey: "workStartDate",
            name: "Work start",
            header: "Work start",
            cell: WorkStartDateCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.WORK_START_ASC,
                desc: ClientsWorkOverviewSortValue.WORK_START_DESC,
            },
            minSize: 140,
            maxSize: 180,
        },
        [ClientsWorkOverviewColumn.WORK_DELIVERY_DATE]: {
            accessorKey: "workDeliveryDate",
            name: "Work end",
            header: "Work end",
            cell: WorkDeliveryDateCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.WORK_DELIVERY_DATE_ASC,
                desc: ClientsWorkOverviewSortValue.WORK_DELIVERY_DATE_DESC,
            },
            minSize: 140,
            maxSize: 180,
        },
        [ClientsWorkOverviewColumn.CLOSE_STAGE]: {
            accessorKey: "closeStageName",
            name: "Stage",
            header: "Stage",
            cell: WorkCloseStageCell,
            minSize: 160,
            maxSize: 240,
        },
        [ClientsWorkOverviewColumn.TIME_IN_STAGE]: {
            accessorKey: "closeStageSetAt",
            name: "Days in stage",
            header: "Days in stage",
            cell: WorkTimeInStageCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.DAYS_IN_STAGE_ASC,
                desc: ClientsWorkOverviewSortValue.DAYS_IN_STAGE_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ClientsWorkOverviewColumn.PREPARER_REVIEWER]: {
            accessorKey: "preparers",
            name: "Engagement team",
            header: "Engagement team",
            cell: WorkPreparerReviewerCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.ENGAGEMENT_TEAM_COUNT_ASC,
                desc: ClientsWorkOverviewSortValue.ENGAGEMENT_TEAM_COUNT_DESC,
            },
            minSize: 160,
            maxSize: 260,
        },
        // Offered through workPageViewConfig.
        [ClientsWorkOverviewColumn.MY_ROLE]: {
            accessorKey: "preparers",
            name: "My role",
            header: "My role",
            cell: WorkMyRoleCell,
            internal: true,
            hiddenFromSettings: true,
            minSize: 140,
            maxSize: 240,
        },
        [ClientsWorkOverviewColumn.TASK_PROGRESS]: {
            accessorKey: "terminatedTasksCount",
            name: "Progress",
            header: "Progress",
            cell: WorkTaskProgressCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.PROGRESS_ASC,
                desc: ClientsWorkOverviewSortValue.PROGRESS_DESC,
            },
            headerAction: {
                type: "variantMenu",
                optionKey: "variant",
                defaultValue: "count",
                options: [
                    { value: "count", label: "Value" },
                    { value: "percentage", label: "Percentage" },
                    { value: "circle", label: "Circle" },
                    { value: "remaining", label: "Remaining" },
                ],
            },
            minSize: 120,
            maxSize: 180,
        },
        [ClientsWorkOverviewColumn.ESTIMATED_TIME]: {
            accessorKey: "estimatedTimeRemaining",
            name: "Estimated time",
            header: "Estimated time",
            cell: WorkEstimatedTimeCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.ESTIMATED_TIME_ASC,
                desc: ClientsWorkOverviewSortValue.ESTIMATED_TIME_DESC,
            },
            headerAction: {
                type: "variantMenu",
                optionKey: "variant",
                defaultValue: DEFAULT_ESTIMATED_TIME_CELL_VARIANT,
                options: [
                    {
                        value: "remainingOfTotal",
                        label: "Remaining / total",
                    },
                    {
                        value: "completedOfTotal",
                        label: "Completed / total",
                    },
                    {
                        value: "remainingOfCompleted",
                        label: "Remaining / completed",
                    },
                ],
            },
            align: "right",
            minSize: 160,
            maxSize: 220,
        },
        [ClientsWorkOverviewColumn.APPLICATION_TYPE]: {
            accessorKey: "applicationType",
            name: "Application type",
            header: "Application type",
            cell: WorkApplicationTypeCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.APPLICATION_TYPE_ASC,
                desc: ClientsWorkOverviewSortValue.APPLICATION_TYPE_DESC,
            },
            minSize: 120,
            maxSize: 160,
        },
        [ClientsWorkOverviewColumn.APPLIED_BY]: {
            accessorKey: "createdByUserId",
            name: "Applied by",
            header: "Applied by",
            cell: WorkAppliedByCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.APPLIED_BY_ASC,
                desc: ClientsWorkOverviewSortValue.APPLIED_BY_DESC,
            },
            minSize: 160,
            maxSize: 240,
        },
        [ClientsWorkOverviewColumn.APPLIED_AT]: {
            accessorKey: "createdAt",
            name: "Applied at",
            header: "Applied at",
            cell: WorkAppliedAtCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.APPLIED_AT_ASC,
                desc: ClientsWorkOverviewSortValue.APPLIED_AT_DESC,
            },
            minSize: 160,
            maxSize: 220,
        },
        [ClientsWorkOverviewColumn.HEALTH]: {
            accessorKey: "health",
            name: "Health",
            header: "Health",
            cell: WorkHealthCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.HEALTH_ASC,
                desc: ClientsWorkOverviewSortValue.HEALTH_DESC,
            },
            minSize: 120,
            maxSize: 160,
        },
        [ClientsWorkOverviewColumn.UNREAD_TASKS]: {
            accessorKey: "unreadTaskCount",
            name: "Unread tasks",
            header: "Unread tasks",
            cell: WorkUnreadTasksCell,
            sort: {
                asc: ClientsWorkOverviewSortValue.UNREAD_TASKS_ASC,
                desc: ClientsWorkOverviewSortValue.UNREAD_TASKS_DESC,
            },
            minSize: 120,
            maxSize: 160,
        },
        [ClientsWorkOverviewColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: WorkActionsCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 120,
            maxSize: 120,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: ClientsWorkOverviewColumn.CLIENT,
        childrenKey: "children",
    },
};
